import React, { useState, useMemo } from "react";
import { Input, Table } from "antd";
import { collection, getDocs } from "firebase/firestore";
import { useQuery } from "@tanstack/react-query";
import SuperAdminContainer from "../SuperAdminContainer";
import { ScreenHeader } from "../../../CommonComponents";
import { db } from "../../../services/firebaseService/connection";

function getLocalDayBounds(daysAgo = 0) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function buildTimeBoundaries() {
  const todayStart = getLocalDayBounds(0);
  const last7Start = getLocalDayBounds(6);
  const last30Start = getLocalDayBounds(29);
  return { todayStart, last7Start, last30Start };
}

async function fetchRegistrationsData() {
  const groupsSnap = await getDocs(collection(db, "group"));

  const groupDocs = [];
  groupsSnap.forEach((doc) => {
    groupDocs.push({ groupId: doc.id, ...doc.data() });
  });

  if (groupDocs.length === 0) {
    console.warn("[Registrations] No groups found, returning empty.");
    return [];
  }

  const { todayStart, last7Start, last30Start } = buildTimeBoundaries();

  const memberFetches = groupDocs.map(
    async ({ groupId, groupName, clubName }) => {
      const membersSnap = await getDocs(
        collection(db, "userGroup", groupId, "members"),
      );

      let total = 0;
      let today = 0;
      let last7 = 0;
      let last30 = 0;

      membersSnap.forEach((doc) => {
        const { addedOn } = doc.data();
        const ts = typeof addedOn === "number" ? addedOn : Number(addedOn);

        total += 1;
        if (ts >= last30Start) last30 += 1;
        if (ts >= last7Start) last7 += 1;
        if (ts >= todayStart) today += 1;
      });

      return { groupId, groupName, clubName, total, today, last7, last30 };
    },
  );

  const results = await Promise.all(memberFetches);

  const filtered = results.filter((r) => r.last30 > 0);

  return filtered;
}

export default function Registrations() {
  const [filters, setFilters] = useState({ search: "" });

  const { data: tableData = [], isLoading } = useQuery({
    queryKey: ["superadmin-registrations"],
    queryFn: fetchRegistrationsData,
    staleTime: 5 * 60 * 1000, // treat as fresh for 5 mins — no refetch on tab switch
    gcTime: 10 * 60 * 1000, // keep in cache for 10 mins after unmount
    refetchOnWindowFocus: false,
  });

  const filteredData = useMemo(() => {
    const search = filters.search?.toLowerCase().trim();
    if (!search) return tableData;
    return tableData.filter(
      (row) =>
        row.groupName?.toLowerCase().includes(search) ||
        row.clubName?.toLowerCase().includes(search),
    );
  }, [tableData, filters.search]);

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, row) => {
        acc.total += row.total || 0;
        acc.today += row.today || 0;
        acc.last7 += row.last7 || 0;
        acc.last30 += row.last30 || 0;
        return acc;
      },
      { total: 0, today: 0, last7: 0, last30: 0 },
    );
  }, [filteredData]);

  const columns = useMemo(
    () => [
      {
        title: "Group",
        dataIndex: "groupName",
        key: "groupName",
        sorter: (a, b) => a.groupName?.localeCompare(b.groupName),
      },
      {
        title: "Club",
        dataIndex: "clubName",
        key: "clubName",
        sorter: (a, b) => a.clubName?.localeCompare(b.clubName),
      },
      {
        title: `Total (${totals.total})`,
        dataIndex: "total",
        key: "total",
        sorter: (a, b) => a.total - b.total,
      },
      {
        title: `Today (${totals.today})`,
        dataIndex: "today",
        key: "today",
        sorter: (a, b) => a.today - b.today,
      },
      {
        title: `Last 7 days (${totals.last7})`,
        dataIndex: "last7",
        key: "last7",
        sorter: (a, b) => a.last7 - b.last7,
      },
      {
        title: `Last 30 days (${totals.last30})`,
        dataIndex: "last30",
        key: "last30",
        defaultSortOrder: "descend",
        sorter: (a, b) => a.last30 - b.last30,
      },
    ],
    [totals.total, totals.today, totals.last7, totals.last30],
  );

  return (
    <SuperAdminContainer>
      <ScreenHeader title="Registrations" elementCount={filteredData.length} />
      <div
        style={{
          marginTop: "2px",
          backgroundColor: "var(--primary-white-background)",
          padding: "8px 12px",
        }}
      >
        <Input
          style={{ maxWidth: "350px" }}
          value={filters.search}
          onChange={(e) => {
            const searchValue = e.target.value;
            setFilters((prev) => ({ ...prev, search: searchValue }));
          }}
          allowClear
          name="search"
          placeholder="Search by group or club"
          tabIndex="2"
        />
      </div>

      <Table
        columns={columns}
        bordered
        dataSource={filteredData}
        loading={isLoading}
        rowKey="groupId"
        bodyStyle={{
          backgroundColor: "#ffffff",
        }}
        scroll={{ x: true }}
        pagination={{
          position: ["bottomLeft"],
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        className="table-backdrop mb-3"
      />
    </SuperAdminContainer>
  );
}
